import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { AgentMark } from '../ui/AgentMark';
import { ProcessingTag } from '../ui/ProcessingTag';
import { useDb } from '../../hooks/useDb';
import { useMotionPreference } from '../../hooks/useMotionPreference';
import { getAgent } from '../../utils/agents';
import type { AgentId } from '../../types/anna';
import type { AgentTask, DeskPanelKind } from '../../types/db';
const EASE = [0.23, 1, 0.32, 1] as const;
const LIVE = new Set<AgentId>(['code', 'mail', 'studio']);
function useStream(count: number, everyMs: number) {
  const [n, setN] = useState(1);
  useEffect(() => {
    if (n >= count) return;
    const id = window.setTimeout(() => setN(v => v + 1), everyMs);
    return () => window.clearTimeout(id);
  }, [n, count, everyMs]);
  return n;
}
function LiveHeader({
  task,
  live
}: {
  task?: AgentTask;
  live: boolean;
}) {
  if (!task) return null;
  const a = getAgent(task.agent);
  return <header className="flex items-center gap-3" data-id="element-653" data-mp="29v8jsg954r2i">
      <AgentMark agentId={task.agent} data-id="element-654" data-mp="25twr21uyzpq3" />
      <div className="min-w-0 flex-1" data-id="element-655" data-mp="25uy3a9nq2i9e">
        <p className="truncate text-[15px] font-medium text-ink" data-id="element-656" data-mp="20vd3jx0qa5vm">
          <span style={{
          color: a.color
        }} data-id="element-657" data-mp="20tih2e9t65pf">{a.name}</span> · {task.title}
        </p>
        <p className="text-[13px] text-ink-muted" data-id="element-658" data-mp="1vvgz5wo4ccfj">
          {task.model} · stap {task.step}/{task.totalSteps}
        </p>
      </div>
      {live && <span className="flex items-center gap-1.5 text-[13px] text-ink-soft" data-id="element-659" data-mp="1m6pbi0q3ceaf">
          <span className="h-1.5 w-1.5 rounded-full" style={{
        backgroundColor: a.color
      }} aria-hidden data-id="element-660" data-mp="1m4maymrbb2rg" />
          live
        </span>}
      <ProcessingTag where={task.where} data-id="element-661" data-mp="1d6k43qkwdfw9" />
    </header>;
}
const openBtn = 'inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full border border-white/10 px-3.5 text-[13px] font-medium text-ink transition-colors duration-150 hover:border-white/25';
function CodeLive({
  onOpen
}: {
  onOpen: (kind: DeskPanelKind) => void;
}) {
  const reduce = useMotionPreference();
  const {
    lines,
    task,
    pr
  } = useDb(t => ({
    lines: t.liveLogs.find(l => l.agent === 'code')?.lines ?? [],
    task: t.agentTasks.find(a => a.agent === 'code'),
    pr: t.pullRequests[0]
  }));
  const n = useStream(lines.length, 1500);
  const shown = lines.slice(0, n);
  const passed = shown.reduce((s, l) => s + (l.tests ?? 0), 0);
  const total = pr.tests.passed + pr.tests.failed;
  const done = n >= lines.length;
  return <article className="glass flex flex-col rounded-3xl p-5 2xl:p-6" data-id="element-662" data-mp="1h88l8o3fdszv">
      <LiveHeader task={task} live={!done} data-id="element-663" data-mp="13iybzfu3ll10" />
      <div className="mt-4 min-h-[260px] flex-1 overflow-hidden rounded-2xl bg-night-950/70 p-4 font-mono text-[13px] leading-6" aria-live="polite" data-id="element-664" data-mp="13hwq0gbjne4i">
        <AnimatePresence initial={false} data-id="element-665" data-mp="1v8t4fld95qtw">
          {shown.slice(-10).map(l => <motion.p key={l.text} layout={!reduce} initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.2,
          ease: EASE
        }} className={l.text.startsWith('PASS') ? 'text-[#8FD99A]' : l.text.startsWith('$') ? 'text-ink' : 'text-ink-soft'} data-id="element-666" data-mp="1zzszel2t9ytr">
              {l.text}
            </motion.p>)}
        </AnimatePresence>
        {!done && <span className="mt-1 inline-block h-4 w-2 bg-ink-soft" aria-hidden data-id="element-667" data-mp="31rm2p9okonri" />}
      </div>
      <div className="mt-4" data-id="element-668" data-mp="31tf9coz6r7zi">
        <div className="flex items-baseline justify-between text-[13px]" data-id="element-669" data-mp="2wt0zr2hxrpzd">
          <span className="text-ink-soft" data-id="element-670" data-mp="3bps2nlqtjjtq">Tests</span>
          <span className="tabular-nums text-ink" data-id="element-671" data-mp="3bqjmjzb1zt46">
            {passed}/{total} geslaagd
          </span>
        </div>
        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/[0.08]" data-id="element-672" data-mp="36pdtu1he85un">
          <motion.div className="h-full rounded-full bg-[#8FD99A]" initial={false} animate={{
          width: `${passed / Math.max(1, total) * 100}%`
        }} transition={{
          duration: 0.3,
          ease: EASE
        }} data-id="element-673" data-mp="qxzofy32osjn" />
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-3" data-id="element-674" data-mp="2wn1rxqs8qkb6">
        <p className="text-[13px] text-ink-muted" data-id="element-675" data-mp="30pevq9nfg85b">Mergen doe je zelf.</p>
        <button type="button" onClick={() => onOpen('diff')} className={openBtn} data-id="element-676" data-mp="30rj8zi77uji9">
          Toon diff <ArrowUpRightIcon className="h-3.5 w-3.5" aria-hidden data-id="element-677" data-mp="35pt6hzgexw71" />
        </button>
      </div>
    </article>;
}
function MailLive({
  onOpen
}: {
  onOpen: (kind: DeskPanelKind) => void;
}) {
  const reduce = useMotionPreference();
  const {
    draft,
    task
  } = useDb(t => ({
    draft: t.mailDrafts.find(d => d.id === 'd-bakker-reply'),
    task: t.agentTasks.find(a => a.agent === 'mail')
  }));
  const body = draft?.body ?? '';
  const [chars, setChars] = useState(reduce ? body.length : 0);
  useEffect(() => {
    if (chars >= body.length) return;
    const id = window.setTimeout(() => setChars(c => Math.min(body.length, c + 2)), 90);
    return () => window.clearTimeout(id);
  }, [chars, body.length]);
  const done = chars >= body.length;
  return <article className="glass flex flex-col rounded-3xl p-5" data-id="element-678" data-mp="7blhwchc9yvm">
      <LiveHeader task={task} live={!done} data-id="element-679" data-mp="2dk15y7dybnb" />
      <p className="mt-4 text-[13px] text-ink-muted" data-id="element-680" data-mp="2ebjw8fvwew6">Aan klant Bakker · {draft?.subject}</p>
      <p className="mt-1 min-h-[72px] border-l-2 border-accent/50 pl-3 text-[15px] leading-relaxed text-ink" data-id="element-681" data-mp="3uie5fxwuxqu5">
        <span className="sr-only" data-id="element-682" data-mp="v0wcfc83ix1b">{body}</span>
        <span aria-hidden data-id="element-683" data-mp="q34jcevpt4vj">
          {body.slice(0, chars)}
          {!done && <span className="ml-0.5 inline-block h-4 w-[2px] translate-y-0.5 bg-accent" data-id="element-684" data-mp="q3wejoleh0la" />}
        </span>
      </p>
      <div className="mt-4 flex items-center justify-between gap-3" data-id="element-685" data-mp="labi40zggeo7">
        <p className={`text-[13px] ${done ? 'text-amber' : 'text-ink-muted'}`} data-id="element-686" data-mp="ha16o8sw8w1y">{done ? 'Concept klaar · wacht op jouw ok' : 'Anna schrijft…'}</p>
        {done && <button type="button" onClick={() => onOpen('mail')} className={openBtn} data-id="element-687" data-mp="191gta2ugais6">
            Toon concept <ArrowUpRightIcon className="h-3.5 w-3.5" aria-hidden data-id="element-688" data-mp="339ay18v1mgsf" />
          </button>}
      </div>
    </article>;
}
function StudioLive({
  onOpen
}: {
  onOpen: (kind: DeskPanelKind) => void;
}) {
  const {
    task,
    render,
    gpu
  } = useDb(t => ({
    task: t.agentTasks.find(a => a.agent === 'studio'),
    render: t.renders[0],
    gpu: t.meta.gpu
  }));
  const label = task?.stepLabel ?? '';
  const pct = label.match(/(\d+)%/);
  const wake = label.match(/~(\d+) s/);
  const ready = render.status === 'klaar';
  const waking = !ready && gpu === 'wordt gewekt';
  const progress = ready ? 1 : waking ? wake ? 1 - Number(wake[1]) / 40 : 0.05 : pct ? Number(pct[1]) / 100 : 0;
  return <article className="glass flex flex-col rounded-3xl p-5" data-id="element-689" data-mp="2fjqcci7i95fg">
      <LiveHeader task={task} live={!ready} data-id="element-690" data-mp="2fm341n32eu5p" />
      {ready ? <img src={render.image} alt="Gegenereerd beeld voor de landingspagina" className="mt-4 aspect-video w-full rounded-2xl object-cover" data-id="element-691" data-mp="8v3lwol9r60u" /> : <p className="mt-4 line-clamp-2 text-[13px] text-ink-muted" data-id="element-692" data-mp="1yao32l87yg1z">{render.prompt}</p>}
      <div className="mt-4" data-id="element-693" data-mp="1tbulaglwg2w0">
        <div className="flex items-baseline justify-between text-[13px]" data-id="element-694" data-mp="1tcmbfbwq98ew">
          <span className={waking ? 'text-amber' : 'text-ink-soft'} data-id="element-695" data-mp="280h44saqjcrz">{ready ? 'Render klaar' : waking ? 'GPU-brein wordt gewekt' : 'Render'}</span>
          <span className="tabular-nums text-ink" data-id="element-696" data-mp="238fuzf8v6266">{waking ? wake ? `nog ~${wake[1]} s` : '…' : `${Math.round(progress * 100)}%`}</span>
        </div>
        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/[0.08]" data-id="element-697" data-mp="2d03qpscge5dg">
          <motion.div className={`h-full rounded-full ${waking ? 'bg-amber' : 'bg-accent'}`} initial={false} animate={{
          width: `${Math.max(2, progress * 100)}%`
        }} transition={{
          duration: 0.3,
          ease: EASE
        }} data-id="element-698" data-mp="2d26iagt986ka" />
        </div>
      </div>
      {ready && <div className="mt-4 flex justify-end" data-id="element-699" data-mp="rmqw5aghw4d2">
          <button type="button" onClick={() => onOpen('render')} className={openBtn} data-id="element-700" data-mp="ro20bbdure4p">
            Toon beeld <ArrowUpRightIcon className="h-3.5 w-3.5" aria-hidden data-id="element-701" data-mp="5ssauflo4h3e" />
          </button>
        </div>}
    </article>;
}
function OtherAgents() {
  const tasks = useDb(t => t.agentTasks.filter(a => !LIVE.has(a.agent)));
  return <ul className="grid gap-3 border-t border-white/[0.06] pt-4 md:grid-cols-3" data-id="element-702" data-mp="ki7yhmpwk6ev">
      {tasks.map(t => {
      const a = getAgent(t.agent);
      return <li key={t.id} className="min-w-0" data-id="element-703" data-mp="3j1enitxejskx">
            <p className="flex items-center gap-2 text-[14px] text-ink" data-id="element-704" data-mp="3nyog2ig38kcz">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{
            backgroundColor: a.color
          }} aria-hidden data-id="element-705" data-mp="3o0rhdymoxzx0" />
              <span className="font-medium" data-id="element-706" data-mp="1rghq03l5dqg">{a.name}</span>
              <span className={`truncate ${t.status === 'wacht' ? 'text-amber' : 'text-ink-soft'}`} data-id="element-707" data-mp="1t1cnqttayou">{t.stepLabel}</span>
            </p>
            <p className="mt-0.5 truncate pl-3.5 font-mono text-[13px] text-ink-muted" data-id="element-708" data-mp="1d6vcqyc8hvix">{t.events[t.events.length - 1]}</p>
          </li>;
    })}
    </ul>;
}

/** The stage is never empty: when nothing is summoned, it shows the agents' live work. */
export function LiveWorkStage({
  onOpen
}: {
  onOpen: (kind: DeskPanelKind) => void;
}) {
  return <section aria-label="Live: wat de agents nu doen" className="flex flex-col gap-5" data-id="element-709" data-mp="2nxjpr9090coq">
      <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-710" data-mp="2iz8gazdv8471">
        <h2 className="text-[16px] font-semibold text-ink" data-id="element-711" data-mp="2e16voa0sjwtg">Nu aan het werk</h2>
        <p className="text-[13px] text-ink-muted" data-id="element-712" data-mp="29zlck6xnymat">Vraag Anna iets om het podium te vullen</p>
      </div>
      <div className="grid gap-5 2xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]" data-id="element-713" data-mp="2500ahnn75xtv">
        <CodeLive onOpen={onOpen} data-id="element-714" data-mp="2015jvzizn4z7" />
        <div className="flex flex-col gap-5" data-id="element-715" data-mp="201yr11luzwhv">
          <MailLive onOpen={onOpen} data-id="element-716" data-mp="2039zvfu5vufu" />
          <StudioLive onOpen={onOpen} data-id="element-717" data-mp="39f3wh67o1rs" />
        </div>
      </div>
      <OtherAgents data-id="element-718" data-mp="36j7ixa0hl5g" />
    </section>;
}